// Integer arithmetic and number formatting (IMPLEMENTATION §0.1, §2.1 core/num).
// Division: every helper floors. Math.floor(a / b) is exact for |a| ≤ 2^53 and integer b > 0 (a non-integer
// quotient is ≥ 1/b away from an integer, far more than half an ulp). C#: use a floor division helper
// (C# `/` truncates toward zero) — the sim only divides non-negative values today.
import type { Int } from './types.ts';

/** floor(a / b) for b > 0. */
export function floorDiv(a: Int, b: Int): Int {
  return Math.floor(a / b);
}

/** floor(a·b / c) for c > 0; a·b must stay within ±2^53. */
export function mulDivFloor(a: Int, b: Int, c: Int): Int {
  return Math.floor((a * b) / c);
}

/** floor(v · p / 1000) for a permille factor p. */
export function applyPermille(v: Int, p: Int): Int {
  return mulDivFloor(v, p, 1000);
}

/** Gallery payout per shot (§0.1): floor((V·Mp + 500) / 1000). */
export function payoutOf(value: Int, multPermille: Int): Int {
  return floorDiv(value * multPermille + 500, 1000);
}

export interface NumFormat {
  /** Significant figures at 1,000 and above (ui.json numberFormat.sigFigs). */
  sigFigs: Int;
  /** Group suffixes from 10^0 (ui.json numberFormat.suffixes: "", K, M, B, T); then aa, ab, … */
  suffixes: readonly string[];
}

function letterSuffix(i: Int): string {
  return String.fromCharCode(97 + (floorDiv(i, 26) % 26)) + String.fromCharCode(97 + (i % 26));
}

/**
 * Formats an integer for the HUD (UX §4 HUD): below 1,000 as is, else `sigFigs` significant figures with
 * a group suffix (1.23K, 45.6M, 7.00T, 1.00aa). TRUNCATES (never rounds up) so the display never overstates
 * cash, and KEEPS trailing zeros (1.00K, 1.50K) so the width stays fixed as the value grows (UX §4 HUD:
 * "3 significant figures … so it doesn't jitter"; M2 decision 13).
 */
export function formatNum(n: Int, fmt: NumFormat): string {
  const sign = n < 0 ? '-' : '';
  const a = n < 0 ? -n : n;
  if (a < 1000) return sign + String(a);
  let base = 1;
  let g = 0;
  while (a >= base * 1000) {
    base *= 1000;
    g++;
  }
  const ip = floorDiv(a, base);
  const digits = ip >= 100 ? 3 : ip >= 10 ? 2 : 1;
  let decimals = fmt.sigFigs - digits;
  if (decimals < 0) decimals = 0;
  if (decimals > 3 * g) decimals = 3 * g;
  let unit = base;
  for (let i = 0; i < decimals; i++) unit = floorDiv(unit, 10);
  const frac = decimals > 0 ? String(floorDiv(a - ip * base, unit)).padStart(decimals, '0') : '';
  const suffix =
    g < fmt.suffixes.length ? (fmt.suffixes[g] as string) : letterSuffix(g - fmt.suffixes.length);
  return `${sign}${ip}${frac.length > 0 ? `.${frac}` : ''}${suffix}`;
}
